// Date utility functions for consistent date handling across the app
// Uses local timezone and European date format (DD/MM/YYYY)

/**
 * Get current date in local timezone as YYYY-MM-DD format
 * This ensures no timezone conversion issues
 */
export const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Get current date in local timezone as DD/MM/YYYY format (European format)
 */
export const getEuropeanDateString = (date = new Date()) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Format date to European format (DD/MM/YYYY)
 * @param {Date|string} date - Date object or date string
 */
export const formatToEuropean = (date) => {
  const dateObj = typeof date === 'string' ? new Date(date + 'T12:00:00') : date;
  return getEuropeanDateString(dateObj);
};

/**
 * Get current time in local timezone as HH:MM format
 */
export const getLocalTimeString = (date = new Date()) => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

/**
 * Get current datetime in local timezone as YYYY-MM-DDTHH:MM:SS format
 */
export const getLocalDateTimeString = (date = new Date()) => {
  const dateStr = getLocalDateString(date);
  const timeStr = getLocalTimeString(date);
  return `${dateStr}T${timeStr}:00`;
};

/**
 * Create a date object from a date string using local timezone
 * @param {string} dateString - Date string in YYYY-MM-DD format
 */
export const createLocalDate = (dateString) => {
  // Add time component to avoid timezone issues
  return new Date(dateString + 'T12:00:00');
};

/**
 * Get day of week name in lowercase (monday, tuesday, etc.)
 * @param {Date} date - Date object
 */
export const getDayOfWeek = (date = new Date()) => {
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return dayNames[date.getDay()];
};

/**
 * Get week dates starting from Monday for a given date
 * @param {Date} date - Date to get week for
 */
export const getWeekDates = (date = new Date()) => {
  const startOfWeek = new Date(date);
  const dayOfWeek = startOfWeek.getDay();
  const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  startOfWeek.setDate(startOfWeek.getDate() + daysToMonday);
  
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(startOfWeek);
    date.setDate(startOfWeek.getDate() + i);
    dates.push(date);
  }
  
  return dates;
};

/**
 * Check if two dates are the same day (ignoring time)
 * @param {Date} date1 - First date
 * @param {Date} date2 - Second date
 */
export const isSameDay = (date1, date2) => {
  return date1.getFullYear() === date2.getFullYear() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getDate() === date2.getDate();
};

/**
 * Get relative date string (Today, Yesterday, Tomorrow, or formatted date)
 * @param {Date} date - Date to format
 */
export const getRelativeDateString = (date) => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  if (isSameDay(date, today)) {
    return 'Today';
  } else if (isSameDay(date, yesterday)) {
    return 'Yesterday';
  } else if (isSameDay(date, tomorrow)) {
    return 'Tomorrow';
  } else {
    return formatToEuropean(date);
  }
};

/**
 * Parse European date format (DD/MM/YYYY) to Date object
 * @param {string} dateString - Date string in DD/MM/YYYY format
 */
export const parseEuropeanDate = (dateString) => {
  const [day, month, year] = dateString.split('/');
  return new Date(year, month - 1, day);
};

/**
 * Validate European date format
 * @param {string} dateString - Date string to validate
 */
export const isValidEuropeanDate = (dateString) => {
  const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const match = dateString.match(regex);
  if (!match) return false;
  
  const [, day, month, year] = match;
  const date = new Date(year, month - 1, day);
  return date.getDate() == day && date.getMonth() == month - 1 && date.getFullYear() == year;
};
